import './quote.css';

import classNames from 'classnames';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import Image from 'gatsby-plugin-sanity-image';
import React, { useEffect, useRef, useState } from 'react';
import { FaPlay } from 'react-icons/fa6';
import ReactPlayer from 'react-player';

import * as linkStyles from '../alma-link/alma-link.module.css';
import { PortableText } from '../portableText';

export function QuoteModule({
	_rawQuoteContext,
	_rawRichQuote,
	award,
	attributionColor,
	enableAward,
	enableVideo,
	link,
	maxWidth,
	quote,
	quoteColor,
	quoteContext,
	richQuote,
	sourceImage,
	subheader,
	videoCta,
	videoFile,
	videoType,
	videoUrl,
	widthClass,
}) {
	const overlayRef = useRef(null);
	const playerRef = useRef(null);

	// state
	const [isVideoOpen, setIsVideoOpen] = useState(false);

	const imageData = getImage(sourceImage?.asset);

	// Function to calculate total character count from rich text blocks
	const getRichQuoteLength = (richText) => {
		if (!richText) return 0;

		// Flatten the rich text into a single string and calculate the length
		const textContent = richText
			.map((block) => block.children?.map((child) => child.text).join(''))
			.join(''); // Join all the text content from all blocks
		return textContent.length;
	};

	const quoteLength = (quote || '').length;
	const richQuoteLength = getRichQuoteLength(richQuote || _rawRichQuote);

	const openVideo = () => setIsVideoOpen(true);
	const closeVideo = () => setIsVideoOpen(false);

	// Handle Escape key to close
	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key === 'Escape') closeVideo();
		};
		if (isVideoOpen) {
			window.addEventListener('keydown', handleKeyDown);
		}
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [isVideoOpen]);

	// Handle outside click to close
	const handleOutsideClick = (e) => {
		if (e.target === overlayRef.current) {
			closeVideo();
		}
	};

	return (
		<div
			className={classNames(
				'quote-container',
				maxWidth && maxWidth,
				widthClass
			)}
		>
			{enableVideo && imageData && (
				<GatsbyImage
					alt={sourceImage?.alt || ''}
					className={'video-image'}
					image={imageData}
				/>
			)}
			<div className='quote-inner'>
				{!enableVideo && <img src='/quote.svg' className='quote-svg' />}
				{((enableVideo && quoteContext) || _rawQuoteContext) && (
					<PortableText
						className='quote-context'
						blocks={quoteContext || _rawQuoteContext}
					/>
				)}
				{quote && (
					<p
						className={classNames(
							'quote-text',
							quoteColor && quoteColor,
							quoteLength > 100 ? 'long-quote' : 'short-quote'
						)}
					>
						{quote}
					</p>
				)}
				{(richQuote || _rawRichQuote) && (
					<PortableText
						className={classNames(
							'quote-text',
							richQuoteLength > 100 ? 'long-quote' : 'short-quote'
						)}
						blocks={richQuote || _rawRichQuote}
					/>
				)}
				{subheader && (
					<div className='attr-wrapper'>
						{!enableVideo && imageData && (
							<GatsbyImage
								alt={sourceImage?.alt || ''}
								className={'source-image'}
								image={imageData}
							/>
						)}
						<p
							className={classNames(
								'testimonial-attr',
								attributionColor ? attributionColor : 'cdgr'
							)}
						>
							&mdash;{' '}
							{link ? (
								<a
									target='_blank'
									rel='noopener noreferrer'
									href={link}
								>
									{subheader}
								</a>
							) : (
								subheader
							)}
						</p>
					</div>
				)}
				{enableAward && (
					<div className='award-block aic bcbge p075'>
						<div className='mr1'>
							{award.icon?.asset && (
								<Image
									{...award.icon}
									loading='lazy'
									alt={award.icon?.alt || ''}
								/>
							)}
						</div>
						<p className='pr15 body-s'>{award.description}</p>
					</div>
				)}
				{enableVideo && (
					<>
						<button
							className={classNames(
								linkStyles.linkContainer,
								'cxdsb',
								'video-button'
							)}
							onClick={openVideo}
						>
							<div className='video-icon-wrapper'>
								<FaPlay></FaPlay>
							</div>
							{videoCta}
						</button>

						{isVideoOpen && (
							<div
								id='overlay'
								className='overlay'
								ref={overlayRef}
								onClick={handleOutsideClick}
							>
								<div className='video-wrapper'>
									<button
										className='close'
										onClick={closeVideo}
									>
										Hide Video
									</button>
									<ReactPlayer
										className='react-player'
										controls={true}
										height='100%'
										playIcon={<></>}
										playing={isVideoOpen}
										playsinline={true}
										ref={playerRef}
										url={
											videoType === 'youtube'
												? videoUrl
												: videoFile?.asset?.url
										}
										width='100%'
									/>
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}
